import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import FinanceShell from "@/components/finance/FinanceShell";
import Pagination from "@/components/Pagination";
import { formatTRY, formatDate, REFUND_STATUS_LABELS, REFUND_STATUS_COLORS, METHOD_LABELS } from "@/components/finance/financeLabels";
import { CheckCircle2, XCircle, Send, X, FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const PAGE_SIZE = 50;
const APPROVE_ROLES = new Set(["super_admin", "finance_approver"]);

export default function RefundsPage() {
    const { user } = useAuth() || {};
    const isApprover = user && APPROVE_ROLES.has(user.role);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [counts, setCounts] = useState({});
    const [page, setPage] = useState(1);
    const [tab, setTab] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [cashAccounts, setCashAccounts] = useState([]);
    const [actionRefund, setActionRefund] = useState(null);
    const [actionType, setActionType] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE });
            if (tab) params.set("status", tab);
            const r = await api.get(`/admin/finance/refunds?${params.toString()}`);
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
            setCounts(r.data.counts || {});
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, page]);
    useEffect(() => { setPage(1); }, [tab]);
    useEffect(() => { api.get("/admin/finance/cash-accounts").then((r) => setCashAccounts(r.data || [])); }, []);

    const handleAction = async (type, refund, payload) => {
        try {
            await api.post(`/admin/finance/refunds/${refund.id}/${type}`, payload);
            toast.success(type === "approve" ? "Onaylandı" : type === "reject" ? "Reddedildi" : "İade işlendi");
            setActionRefund(null); setActionType(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Hata");
        }
    };

    return (
        <FinanceShell>
            <div className="flex items-center gap-1 mb-4">
                {["pending", "approved", "processed", "rejected"].map((s) => (
                    <button key={s} onClick={() => setTab(s)} data-testid={`refund-tab-${s}`}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${tab === s ? "bg-sky-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"}`}>
                        {REFUND_STATUS_LABELS[s]} <span className="opacity-70">({counts[s] || 0})</span>
                    </button>
                ))}
            </div>

            {!isApprover && tab === "pending" && (
                <div className="mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-xs flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <span>İade onayı için <b>super_admin</b> veya <b>finance_approver</b> rolü gereklidir. Siz iade <b>talep oluşturabilirsiniz</b> ancak onaylayamazsınız.</span>
                </div>
            )}

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                {loading ? <div className="py-10 text-center text-slate-500">Yükleniyor...</div> : items.length === 0 ? (
                    <div className="py-12 text-center text-slate-500">Bu kategoride iade kaydı yok.</div>
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {items.map((r) => (
                            <div key={r.id} className="p-4" data-testid={`refund-row-${r.refund_no}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex-1 min-w-[200px]">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <span className="font-mono text-xs text-slate-400">{r.refund_no}</span>
                                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${REFUND_STATUS_COLORS[r.status]}`}>{REFUND_STATUS_LABELS[r.status]}</span>
                                            <span className="text-[10px] text-slate-500">PNR: {r.booking_voucher}</span>
                                        </div>
                                        <div className="font-bold text-slate-900 dark:text-white">{r.customer_name}</div>
                                        <p className="text-xs text-slate-500 mt-1">{r.reason}</p>
                                        <div className="text-[11px] text-slate-400 mt-1">
                                            Politika: {r.penalty_reason} · Talep: {formatDate(r.request_date)}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-2xl font-extrabold text-rose-600">{formatTRY(r.amount_refund)}</div>
                                        <div className="text-[10px] text-slate-400">
                                            Ödenen: {formatTRY(r.amount_paid)} · Ceza ({r.penalty_pct}%): {formatTRY(r.penalty)}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    <a href={`/api/admin/finance/refunds/${r.id}/receipt.pdf`} target="_blank" rel="noreferrer" className="text-xs px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 inline-flex items-center gap-1.5" data-testid={`refund-pdf-${r.refund_no}`}><FileText className="h-3 w-3" /> Makbuz PDF</a>
                                    {isApprover && r.status === "pending" && (
                                        <>
                                            <button onClick={() => { setActionRefund(r); setActionType("approve"); }} className="text-xs px-2.5 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5" data-testid={`refund-approve-${r.refund_no}`}><CheckCircle2 className="h-3 w-3" /> Onayla</button>
                                            <button onClick={() => { setActionRefund(r); setActionType("reject"); }} className="text-xs px-2.5 py-1 rounded-lg bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1.5" data-testid={`refund-reject-${r.refund_no}`}><XCircle className="h-3 w-3" /> Reddet</button>
                                        </>
                                    )}
                                    {isApprover && r.status === "approved" && (
                                        <button onClick={() => { setActionRefund(r); setActionType("process"); }} className="text-xs px-2.5 py-1 rounded-lg bg-sky-600 text-white hover:bg-sky-700 inline-flex items-center gap-1.5" data-testid={`refund-process-${r.refund_no}`}><Send className="h-3 w-3" /> İade Yap</button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <Pagination total={total} page={page} pageSize={PAGE_SIZE} onChange={setPage} label="iade" />
            </div>

            {actionRefund && actionType && (
                <ActionModal refund={actionRefund} type={actionType} cashAccounts={cashAccounts} onClose={() => { setActionRefund(null); setActionType(null); }} onSubmit={(payload) => handleAction(actionType, actionRefund, payload)} />
            )}
        </FinanceShell>
    );
}

function ActionModal({ refund, type, cashAccounts, onClose, onSubmit }) {
    const [form, setForm] = useState({ note: "", rejected_reason: "", target_cash_account_id: "", reference_no: "", method: refund.method || "bank_transfer" });
    const titles = { approve: "İadeyi Onayla", reject: "İadeyi Reddet", process: "İadeyi İşle (Para Çıkışı)" };
    const submit = () => {
        if (type === "approve") onSubmit({ note: form.note });
        else if (type === "reject") {
            if (!form.rejected_reason.trim()) { toast.error("Red gerekçesi gerekli"); return; }
            onSubmit({ rejected_reason: form.rejected_reason });
        } else if (type === "process") {
            if (!form.target_cash_account_id) { toast.error("Kasa hesabı seçin"); return; }
            onSubmit({ target_cash_account_id: form.target_cash_account_id, reference_no: form.reference_no, method: form.method, note: form.note });
        }
    };
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold">{titles[type]}</h3>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="text-sm text-slate-500 mb-3">
                    <b>{refund.customer_name}</b> · {refund.booking_voucher} · İade: <b>{formatTRY(refund.amount_refund)}</b>
                </div>
                {type === "reject" ? (
                    <textarea rows={3} placeholder="Red gerekçesi..." value={form.rejected_reason} onChange={(e) => setForm({ ...form, rejected_reason: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" data-testid="action-reject-reason" />
                ) : type === "process" ? (
                    <div className="space-y-3">
                        <select value={form.target_cash_account_id} onChange={(e) => setForm({ ...form, target_cash_account_id: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" data-testid="action-cash-select">
                            <option value="">Çıkış yapılacak kasa...</option>
                            {cashAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name} ({formatTRY(a.balance)})</option>)}
                        </select>
                        <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm">
                            {Object.entries(METHOD_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        <input placeholder="Referans no (banka dekontu vb.)" value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                        <textarea rows={2} placeholder="Not (opsiyonel)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                    </div>
                ) : (
                    <textarea rows={3} placeholder="Onay notu (opsiyonel)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                )}
                <div className="flex gap-2 justify-end mt-4">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm">İptal</button>
                    <button onClick={submit} className={`px-4 py-2 rounded-lg text-white font-semibold text-sm ${type === "reject" ? "bg-rose-600" : type === "process" ? "bg-sky-600" : "bg-emerald-600"}`} data-testid="action-submit">Onayla</button>
                </div>
            </div>
        </div>
    );
}
